import { useTranslations } from '@vocab/react';
import {
  Button,
  Dialog,
  Inline,
  Notice,
  Stack,
  Text,
  useToast,
} from 'braid-design-system';
import translations from '../ExpireJobDialog/.vocab';
import { useState, type FC } from 'react';
import { UpdateExpiryDateForm } from './UpdateExpiryDateForm';

type FormState =
  | { state: 'loading' }
  | { state: 'error'; errorMessage: string }
  | { state: 'default' };

interface ExpireJobDialogProps {
  open: boolean;
  onClose: () => void;
  expireJob: () => Promise<boolean>;
  isCsView: boolean;
  expireDate: string;
  updateExpiryDate: (expiryDate: Date) => Promise<boolean>;
}

export const ExpireJobDialog: FC<ExpireJobDialogProps> = ({
  open,
  onClose,
  expireJob,
  isCsView,
  expireDate,
  updateExpiryDate,
}) => {
  const { t } = useTranslations(translations);
  const showToast = useToast();
  const [formState, setFormState] = useState<FormState>({ state: 'default' });
  const onExpire = async () => {
    setFormState({ state: 'loading' });
    const success = await expireJob();
    if (success) {
      setFormState({ state: 'default' });
      onClose();
      showToast({
        message: t('This job ad has been expired successfully'),
        tone: 'positive',
      });
    } else {
      setFormState({
        state: 'error',
        errorMessage: t(
          'Sorry, we couldn’t save your changes. Can we try again?',
        ),
      });
    }
  };

  return (
    <Dialog
      title={t('Expire ad?')}
      onClose={onClose}
      id="expire-job-dialog"
      open={open}
    >
      <Stack space="large">
        <Stack space="medium">
          <Text>{t('This will remove it from SEEK immediately.')}</Text>
          <Text>
            {t('To re-post this ad later you will be charged for a new ad.')}
          </Text>
          <Text>
            {t(
              'All other features for managing and communicating with candidates will continue to work as normal.',
            )}
          </Text>
        </Stack>

        <Stack space="small">
          {formState.state === 'error' ? (
            <Notice tone="critical">
              <Text>{formState.errorMessage}</Text>
            </Notice>
          ) : null}
          <Inline space="medium">
            <Button
              data={{ testid: 'expire-job-submit' }}
              type="submit"
              onClick={onExpire}
              loading={formState.state === 'loading'}
              tone="formAccent"
              variant="solid"
            >
              {formState.state === 'loading' ? t('Expiring') : t('Expire')}
            </Button>
            <Button
              data={{ testid: 'expire-job-cancel' }}
              tone="neutral"
              variant="transparent"
              onClick={onClose}
            >
              {t('Cancel')}
            </Button>
          </Inline>
        </Stack>

        {isCsView ? (
          <UpdateExpiryDateForm
            onSubmitSuccess={onClose}
            jobExpireDate={expireDate}
            updateExpiryDate={updateExpiryDate}
          />
        ) : null}
      </Stack>
    </Dialog>
  );
};
